/**
 * Centralized Axios Instance with Sentry Error Tracking
 *
 * All API modules should import this instance instead of raw axios
 * to get automatic error tracking and consistent configuration.
 */

import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import config from "./config";
import { getTenantId, getAuthSource, getAuthToken } from "./apiHelpers";
import { captureApiError, addBreadcrumb } from "../utils/sentry";
import { clearUserData } from "../utils/logout";
import { bearerToken, isUsableToken, persistAuthToken, tokenExpired } from "./authTokenStore";
import { reportSessionRejection } from "./sessionDiagnostics";
import {
  IDLE_SECONDS_HEADER,
  clearStaffIdleSession,
  idleSecondsHeaderValue,
  noteIdleLimitFromResponse,
} from "./staffIdleSession";
import { isPublicPathname } from "../utils/publicRoutes";
import { supabase } from "../utils/supabaseClient";
import { safeRemoveItem } from "@mediatechgroup/app-version";

// When an AUTHENTICATED request comes back 401, the user's token has expired
// or been invalidated server-side. Historically the interceptor just swallowed
// it (see `skipCapture` below), leaving staff stranded on a silently-broken,
// empty page — they couldn't tell their session had lapsed, and (when the
// 2026-06 login rate-limit bug was also in play) couldn't re-auth to fix it.
// Force a clean re-login instead: clear local auth state and bounce to the
// tenant login page with a flag so Login can explain what happened.
//
// Scope is deliberately narrow:
//  - 401 ONLY. 403 is an authorization (permission/RLS) failure for a VALID
//    session — logging those users out would be wrong and disruptive.
//  - Only when a token was actually present. A 401 with no token is just an
//    unauthenticated call (e.g. a public page); there's no session to expire.
//  - Auth-flow endpoints (login, register, password reset, magic link, …) are
//    skipped — those pages render their own 401 (e.g. wrong password) and must
//    not be hijacked into a redirect.
let sessionExpiryRedirectInFlight = false;

const isAuthFlowEndpoint = (url: string): boolean =>
  /\/(login|register|verify-reset-token|verify-magic-link|magic-login|forgot-password|reset-password|search-tenants)\b/.test(
    url
  );

// Core session endpoints — a 401 from one of these means the session
// itself is dead (token rejected by `authenticate`), which is the only
// case where force-logging-out is correct. See the 2026-06-16 incident
// note at the call site: a 401 from an arbitrary data endpoint must NOT
// eject a user who still has a valid session.
const isSessionCriticalEndpoint = (url: string): boolean =>
  /\/auth\/(current-user|capabilities|my-permissions)\b/.test(url);

// Pages a family uses WITHOUT a session: App.tsx's public routes (register,
// sign-in, reset, program and camp listings, embeds...). The app shell still
// asks /auth/capabilities on them whenever a token is in storage, so a family
// whose old session has lapsed sends that dead token there and is refused.
//
// Sentry CLUB-CENTRAL-TENANT-5G / -5H (2026-10): "Session rejected by the
// server", mostly on /register. That refusal used to sign the family out and
// send them to the sign-in page: a family opening Register, or the reset link
// from their email, landed on "your session expired" instead. On these pages
// the dead token is dropped and the page stays. The list is the router's own
// (utils/publicRoutes, held to App.tsx by a parity test), not a hand-picked few.
const isNoSessionPage = (pathname: string): boolean =>
  pathname.includes("/login") || isPublicPathname(pathname);

/**
 * Why a refused session is an EXPECTED end, or null when it is not and must
 * be reported as evidence.
 *
 *  - "idle":    the club's staff idle sign-out (STAFF_IDLE_TIMEOUT, PM aa0f43a7).
 *  - "revoked": "Sign out everywhere" ended it (SESSION_REVOKED, 2026-09-14).
 *  - "lapsed":  the refused token is past its own `exp` (7-day sliding
 *               session, nothing used it for a week) AND it is the token we
 *               hold, or we hold none because an earlier refusal of it on
 *               this page already dropped it.
 *
 * Sentry CLUB-CENTRAL-TENANT-5G / -5H (2026-10): every report after the
 * public-page fix (96f868917, f39568318) was a lapsed token: a member opening
 * view-billing with a token last used 12 days to 4 months earlier, or the
 * SECOND of the app shell's two parallel /auth/capabilities calls (App.tsx and
 * PermissionContext) arriving after the first had already dropped the token,
 * so "stored" read empty and the expected case missed. A lapsed token is
 * refused by design; the member signs in once and is not signed out again.
 *
 * Still reported: a token refused BEFORE its own expiry with no code, and an
 * old token refused while a DIFFERENT, newer token is stored (the GoY
 * "weeks-old token seconds after sign-in" pattern).
 */
export const expectedSessionEnd = (
  rejectedToken: string | null,
  heldToken: string | null,
  flags: { sessionRevoked: boolean; idleSignOut: boolean }
): "idle" | "revoked" | "lapsed" | null => {
  if (flags.idleSignOut) return "idle";
  if (flags.sessionRevoked) return "revoked";
  if (
    isUsableToken(rejectedToken) &&
    tokenExpired(rejectedToken) &&
    (!heldToken || heldToken === rejectedToken)
  ) {
    return "lapsed";
  }
  return null;
};

const currentPathname = (): string => {
  try {
    return window.location.pathname || "";
  } catch {
    return "";
  }
};

// Transient failures (serverless cold-starts, brief windows during a backend
// redeploy) surface as network errors / timeouts / 5xx and make staff-facing
// reports show "failed to load" even though a refresh a second later works
// (this is exactly what happened to the Annual Registration Fee report during
// the 2026-06-30 deploy batch). Auto-retry ONCE, after a short delay, so those
// blips self-heal instead of bothering staff.
//
// Deliberately narrow — this must never re-issue a side-effectful request:
//  - GET only (never POST/PUT/DELETE → no double-charge, no double-email).
//  - Only the read-only paths on RETRYABLE_GET_PATHS below.
//  - Only transient failures: no HTTP response (network/timeout) or a 5xx.
//    4xx (incl. 401/403/404) are real answers and are never retried.
//  - Exactly one retry (guarded by a per-request flag), never offline/cancelled.
const REPORT_RETRY_DELAY_MS = 1200;
const RETRYABLE_STATUSES = [500, 502, 503, 504];

// Read-only GETs where a cold-start blip is most disruptive to staff.
//  - `/reports/`      the heavy staff-facing loads (2026-06-30 deploy batch).
//  - `/programs-slot` the enrollment slot picker. Without a retry a single
//    transient failure empties the class list for the whole page load, which
//    is how a front-desk enrollment died mid-queue on 2026-08-12.
const RETRYABLE_GET_PATHS = ["/reports/", "/programs-slot"];

const shouldRetryTransientGet = (error: AxiosError): boolean => {
  const cfg = error.config as (InternalAxiosRequestConfig & { __reportRetried?: boolean }) | undefined;
  if (!cfg || (cfg.method || "").toLowerCase() !== "get") return false;
  const url = cfg.url || "";
  if (!RETRYABLE_GET_PATHS.some((path) => url.includes(path))) return false;
  if (cfg.__reportRetried) return false;
  if (error.code === "ERR_CANCELED") return false;
  if (error.code === "ERR_NETWORK" && !navigator.onLine) return false;
  const status = error.response?.status;
  const isTransient = !error.response || RETRYABLE_STATUSES.includes(status as number);
  return isTransient;
};

/** Why the session ended, for the sign-in page's message. */
type SessionEndReason = "expired" | "idle";

const handleSessionExpiry = (rejectedToken: string | null, reason: SessionEndReason = "expired"): void => {
  // Guard against the flood of parallel 401s a page fires on mount — clear and
  // redirect exactly once.
  if (sessionExpiryRedirectInFlight) return;
  // Only meaningful if we actually believed we had a session.
  const currentToken = getAuthToken();
  if (!currentToken) return;
  // Only the token we are STILL holding can prove the session is dead.
  //
  // 2026-09-13, Gymnastics of York: Stacy was signed out ~14 times in one
  // evening, "4 more times in 1 minute". This used to eject on a 401 no matter
  // which token the request had carried. A request sent with a token that has
  // since been replaced (a newer slide, a sign-in in another tab), or with no
  // usable token at all (`Bearer null` / "undefined" while storage was being
  // rewritten), 401s for reasons that say nothing about the session we hold —
  // and clearUserData() then wiped that live session, including a sign-in the
  // user had completed seconds earlier.
  if (!isUsableToken(rejectedToken) || rejectedToken !== currentToken) return;
  // On a page that needs no session (sign-in, reset, magic link, the public
  // program listing): don't redirect (on sign-in that would loop; on reset it
  // threw away the family's reset link), but DO drop the dead token.
  // Returning here without removing it kept a weeks-old token in storage for
  // good: every reload of the sign-in page sent it, was refused, and left it
  // there again (Gymnastics of York, reproduced on dev-tenant 2026-09-29).
  if (isNoSessionPage(currentPathname())) {
    try {
      safeRemoveItem("authToken");
    } catch {
      /* storage unavailable */
    }
    return;
  }

  sessionExpiryRedirectInFlight = true;

  // Capture the tenant slug before clearing (clearUserData preserves
  // tenantSlug, but read it up front to be safe).
  const tenantSlug = getTenantId();

  // Drop local auth state immediately so nothing keeps using the dead token,
  // then fire-and-forget the Supabase sign-out — the hard redirect below tears
  // the page down regardless of when it resolves.
  try {
    clearUserData();
    clearStaffIdleSession();
  } catch {
    /* localStorage may be unavailable in some embedded contexts */
  }
  // Wrap in Promise.resolve so a client/mock that returns a non-promise can't
  // throw here; we don't await — the redirect tears the page down regardless.
  //
  // Genuine fire-and-forget, so the no-silent-swallow rule is waived here with
  // cause: there is no caller to inform (we are mid-logout and about to
  // hard-redirect), and a failed remote sign-out must not block the redirect
  // that drops the dead token. This is the intended escape hatch — an explicit,
  // justified disable rather than a quiet swallow.
  // eslint-disable-next-line no-restricted-syntax
  void Promise.resolve(supabase?.auth?.signOut?.()).catch(() => {});

  // "idle" tells the sign-in page this was the club's idle sign-out (PM aa0f43a7).
  const flag = reason === "idle" ? "idle" : "1";
  const target = tenantSlug
    ? `/${tenantSlug}/login?sessionExpired=${flag}`
    : `/login?sessionExpired=${flag}`;
  window.location.assign(target);
};

// A kiosk request carries the KIOSK session's token (api/kioskAPI), which must
// not be replaced by a signed-in user's `authToken`. On a shared front-desk
// computer the club's generic staff account is often signed in in the same
// browser that runs the kiosk; without this opt-out every kiosk request went
// out with the staff token and kioskAuthenticate refused it ("Kiosk
// authentication required") — measured 2026-09-25.
declare module "axios" {
  interface AxiosRequestConfig {
    /** Send the Authorization header the caller set (the kiosk token) unchanged. */
    kioskSession?: boolean;
  }
}

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: config.BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth headers and breadcrumbs
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add tenant ID header
    const tenantId = getTenantId();
    if (tenantId) {
      config.headers.set("x-tenant-id", tenantId);
    }

    // Add auth source header
    const authSource = getAuthSource();
    if (authSource) {
      config.headers.set("x-auth-source", authSource);
    }

    // Add auth token if available — except on a kiosk-session request, whose
    // Authorization is the kiosk token and must stay that way.
    const token = getAuthToken();
    if (token && !config.kioskSession) {
      config.headers.set("Authorization", `Bearer ${token}`);
      // Staff idle sign-out: how long this screen has sat untouched. Sent only
      // once the backend has advertised a limit for this sign-in (see
      // staffIdleSession), so a session without one is exactly as before.
      const idle = idleSecondsHeaderValue();
      if (idle !== null) config.headers.set(IDLE_SECONDS_HEADER, idle);
    }

    // Add breadcrumb for request tracking
    addBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      "api",
      {
        method: config.method,
        url: config.url,
      }
    );

    return config;
  },
  (error: AxiosError) => {
    // Capture request configuration errors
    captureApiError(error, "request_interceptor", {
      component: "axiosInstance",
      operation: "request",
    });
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and capture to Sentry
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Persist the sliding-session token the backend mints on every
    // authenticated response. Login stores a Supabase access token with a ~1h
    // TTL and nothing else refreshes it — we sign in through the backend, not
    // through supabase-js, so there is no client-side Supabase session for
    // `autoRefreshToken` to renew. The backend no longer accepts expired
    // tokens, so this header IS the session.
    //
    // Written through persistAuthToken, which slides at most every 10 minutes
    // per user. Writing every response's newly signed token changed
    // localStorage.authToken every second and re-ran every `[token]` effect —
    // the request storm behind the 2026-09-13 Gymnastics of York sign-outs.
    // It also never stores a malformed value. Storage failures are swallowed
    // there: the request succeeded; the session just won't slide this time.
    persistAuthToken(response.headers?.["x-refreshed-token"]);
    // The staff idle limit the backend sent with it (absent = none).
    noteIdleLimitFromResponse(response.headers as Record<string, unknown> | undefined);

    // Add breadcrumb for successful responses
    addBreadcrumb(
      `API Response: ${response.status} ${response.config.url}`,
      "api",
      {
        status: response.status,
        url: response.config.url,
      }
    );
    return response;
  },
  async (error: AxiosError) => {
    const endpoint = error.config?.url || "unknown";

    // One-shot auto-retry for transient read-only GET failures (see notes above).
    // Re-issue through the instance so it goes back through the interceptors;
    // the retried request is flagged so it can only fire once.
    if (shouldRetryTransientGet(error)) {
      const cfg = error.config as InternalAxiosRequestConfig & { __reportRetried?: boolean };
      cfg.__reportRetried = true;
      addBreadcrumb(
        `API Retry: ${error.response?.status || error.code} ${endpoint}`,
        "api",
        { url: endpoint, status: error.response?.status },
        "warning"
      );
      await new Promise((resolve) => setTimeout(resolve, REPORT_RETRY_DELAY_MS));
      return axiosInstance(cfg);
    }

    // Don't capture certain expected errors
    const status = error.response?.status;

    // Endpoints where 404 is expected behavior (not an error)
    const expected404Endpoints = [
      "/trial/status", // 404 = user has no trial membership (normal)
    ];
    const isExpected404 = status === 404 &&
      expected404Endpoints.some(ep => endpoint.includes(ep));

    const skipCapture =
      // 401/403 are normal auth failures handled by app
      status === 401 ||
      status === 403 ||
      // Expected 404s from specific endpoints
      isExpected404 ||
      // Network errors when offline
      (error.code === "ERR_NETWORK" && !navigator.onLine) ||
      // Request cancelled or aborted (user navigated away)
      error.code === "ERR_CANCELED" ||
      error.code === "ECONNABORTED";

    if (!skipCapture) {
      captureApiError(error, endpoint, {
        component: "axiosInstance",
        operation: "response",
      });
    }

    // Expired/invalidated session → force a clean re-login rather than
    // silently swallowing the 401 and leaving the user on a broken page.
    //
    // INCIDENT MITIGATION 2026-06-16: scope the redirect to CORE SESSION
    // endpoints only. The original blanket "eject on ANY authenticated
    // 401" logged members out the instant a single over-strict data
    // endpoint 401'd right after login — they'd authenticate successfully,
    // get bounced straight back to /login, assume a password problem,
    // reset, and loop. current-user / capabilities / my-permissions are
    // the authoritative "is my session still alive" calls (fired on app
    // load), so a 401 from THOSE is a genuine dead session worth ejecting
    // on; a 401 from an arbitrary data endpoint is not. Once the specific
    // endpoint-level 401 is root-caused this can widen back out (ideally
    // via a real session-death probe rather than an endpoint allowlist).
    //
    // SESSION_REVOKED ("Sign out everywhere", 2026-09-14) is the server saying
    // outright that this session was ended, so it signs the user out from ANY
    // endpoint — a long-lived tab may never call a session endpoint again.
    // handleSessionExpiry still requires the rejected token to be the one we
    // hold, so a revoked OLD token cannot end a newer sign-in.
    //
    // STAFF_IDLE_TIMEOUT (PM aa0f43a7) is the same kind of answer: the club's
    // idle sign-out ended this staff session, from whichever endpoint.
    const rejectionCode = (error.response?.data as { code?: string } | undefined)?.code;
    const sessionRevoked = rejectionCode === "SESSION_REVOKED";
    const idleSignOut = rejectionCode === "STAFF_IDLE_TIMEOUT";
    if (
      status === 401 &&
      (isSessionCriticalEndpoint(endpoint) || sessionRevoked || idleSignOut) &&
      !isAuthFlowEndpoint(endpoint)
    ) {
      const rejectedToken = bearerToken(error.config?.headers);
      const ended = expectedSessionEnd(rejectedToken, getAuthToken(), { sessionRevoked, idleSignOut });
      if (ended) {
        // An expected end of a session is the system working, not an error:
        // keep the trail as a breadcrumb, report nothing.
        addBreadcrumb(
          `Session ended: ${ended}`,
          "session",
          { endpoint, reason: ended, page: currentPathname() },
          "info"
        );
      } else {
        // Evidence (sessionDiagnostics): GoY's shared login was refused a
        // weeks-old token seconds after signing in, source unknown. Whatever
        // is left here is not an expected end, so it still reports.
        reportSessionRejection(rejectedToken, endpoint, rejectionCode);
      }
      handleSessionExpiry(rejectedToken, idleSignOut ? "idle" : "expired");
    }

    // Add breadcrumb for error tracking
    addBreadcrumb(
      `API Error: ${status || error.code} ${endpoint}`,
      "api",
      {
        status,
        url: endpoint,
        message: error.message,
      },
      "error"
    );

    return Promise.reject(error);
  }
);

export default axiosInstance;

/**
 * Sign this screen out because it sat idle past the club's limit (PM
 * aa0f43a7): the same clean sign-out as a refused session, with the idle
 * message on the sign-in page. Called by StaffIdleSignOut.
 */
export const endSessionForIdle = (): void => {
  handleSessionExpiry(getAuthToken(), "idle");
};

/**
 * Create a request without auth headers (for public endpoints)
 */
export const publicAxios = axios.create({
  baseURL: config.BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add error tracking to public axios as well
publicAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const endpoint = error.config?.url || "unknown";

    // Don't capture network errors when offline
    if (!(error.code === "ERR_NETWORK" && !navigator.onLine)) {
      captureApiError(error, endpoint, {
        component: "publicAxios",
        operation: "response",
      });
    }

    return Promise.reject(error);
  }
);
